import { useEffect, useMemo, useState, type FormEvent } from 'react'

import { Banner, Card, CardBody, CardHead, LoadError, PageHead, Skeleton } from '../components/ui.tsx'
import { usePageTitle } from '../layout/AppShell.tsx'
import { put, useApi } from '../lib/api.ts'
import { useCountryOptions } from '../lib/countries.ts'
import { useSession } from '../lib/session.tsx'
import { MAX_NAME_LENGTH, MIN_AGE, profileProblem, type ProfileDetails } from '../../shared/waitlist.ts'
import type { SessionUser } from '../../shared/auth.ts'
import { CURRENCIES, type Currency, type ProfilePayload } from '../../shared/types.ts'

/**
 * The person behind the account: name, date of birth, country and email. Until they save,
 * the form is filled from what they gave when they joined the waitlist or signed in.
 */
export function Settings() {
  usePageTitle('Settings')
  const { setUser } = useSession()
  const countries = useCountryOptions()
  const { data, error, loading, reload } = useApi<ProfilePayload>('/profile')

  const [details, setDetails] = useState<ProfileDetails | null>(null)
  const [currency, setCurrencyChoice] = useState<Currency>('GBP')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (data) {
      setDetails(data.details)
      setCurrencyChoice(data.currency)
    }
  }, [data])

  const latestBirthDate = useMemo(() => {
    const d = new Date()
    d.setFullYear(d.getFullYear() - MIN_AGE)
    return d.toISOString().slice(0, 10)
  }, [])

  if (error) return <main className="content"><LoadError error={error} onRetry={reload} /></main>
  if (loading || !data || !details) {
    return (
      <main className="content">
        <Card>
          <CardBody>
            <Skeleton rows={6} />
          </CardBody>
        </Card>
      </main>
    )
  }

  const set = (field: keyof ProfileDetails) => (value: string) => {
    setDetails((d) => (d ? { ...d, [field]: value } : d))
    setSaved(false)
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const problem = profileProblem(details)
    if (problem) return setErr(problem)
    setErr(null)
    setBusy(true)
    try {
      const res = await put<ProfilePayload & { user: SessionUser }>('/profile', { ...details, currency })
      setDetails(res.details)
      setCurrencyChoice(res.currency)
      setUser(res.user)
      setSaved(true)
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="content">
      <PageHead title="Settings" sub="Your personal details." />

      <div className="grid cols-12">
        <div className="span-6 stack">
          {saved && <Banner tone="success" icon="✓" title="Details saved" />}

          <Card>
            <CardHead title="Profile" />
            <CardBody>
              <form onSubmit={submit} noValidate>
                <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                  <div className="field" style={{ flex: 1 }}>
                    <label htmlFor="st-first">First name</label>
                    <input
                      className="input"
                      id="st-first"
                      autoComplete="given-name"
                      maxLength={MAX_NAME_LENGTH}
                      value={details.firstName}
                      onChange={(e) => set('firstName')(e.target.value)}
                    />
                  </div>
                  <div className="field" style={{ flex: 1 }}>
                    <label htmlFor="st-last">Last name</label>
                    <input
                      className="input"
                      id="st-last"
                      autoComplete="family-name"
                      maxLength={MAX_NAME_LENGTH}
                      value={details.lastName}
                      onChange={(e) => set('lastName')(e.target.value)}
                    />
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="st-email">Email</label>
                  <input className="input" id="st-email" type="email" value={data.email} readOnly disabled />
                  <div className="help">The address you sign in with. It cannot be changed here.</div>
                </div>

                <div className="field">
                  <label htmlFor="st-dob">Date of birth</label>
                  <input
                    className="input"
                    type="date"
                    id="st-dob"
                    autoComplete="bday"
                    max={latestBirthDate}
                    value={details.dateOfBirth}
                    onChange={(e) => set('dateOfBirth')(e.target.value)}
                  />
                </div>

                <div className="field">
                  <label htmlFor="st-country">Country of residence</label>
                  <select
                    className="select"
                    id="st-country"
                    autoComplete="country"
                    value={details.country}
                    onChange={(e) => set('country')(e.target.value)}
                  >
                    <option value="" disabled>
                      Choose a country
                    </option>
                    {countries.map(({ code, name }) => (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="st-currency">Currency</label>
                  <select
                    className="select"
                    id="st-currency"
                    value={currency}
                    onChange={(e) => {
                      setCurrencyChoice(e.target.value as Currency)
                      setSaved(false)
                    }}
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <div className="help">
                    Your bankroll rounds start in this currency.
                    {currency !== data.roundCurrency &&
                      ` Your current round stays in ${data.roundCurrency}; ${currency} applies from your next round.`}
                  </div>
                </div>

                {err && (
                  <div className="field" style={{ marginBottom: 12 }}>
                    <span className="help warn" role="alert">
                      {err}
                    </span>
                  </div>
                )}

                <button type="submit" className="btn primary" disabled={busy}>
                  {busy ? 'Saving…' : 'Save details'}
                </button>
              </form>
            </CardBody>
          </Card>
        </div>
      </div>
    </main>
  )
}
